import React, { useState, useEffect, useMemo } from "react";
import { API_URL } from "../../utils/config";
import { useUserContext } from "../../utils/UserContext";
import TeclaEscEnter from "../../utils/Teclaescenter";
import {
  generateAddress,
  formatDateTime,
} from "../../utils/Utilitats";
import { mapejos, mapToPresentable } from "../../utils/Mapejos";
import { fetchAutenticat, SessioCaducadaError } from "../../utils/auth";
import { tincPermis } from "../../utils/Permisos";
import "./detallsagenda.css";

function DetallsAgenda({
  mode = "consultar", // "nou" | "modificar" | "consultar" | "esborrar"
  item = null, // registre d'agenda (si no "nou")
  onSave,
  onCancel,
  onPrev,
  onNext, // fletxes consultar (wrap al pare)
  pos = null,
  total = null, // comptador "n / total" (opcional)
  forceTasca = false,
  variant = "page",
  onCanviarMode,
  onObrirAjuda,
}) {
  const { empresaSeleccionada, usuariActiu, rolUsuari } = useUserContext();
  const idEmpresa = empresaSeleccionada?.IdEmpresa ?? null;
  const currentUserId = usuariActiu ?? null;
  const esPagina = variant !== "modal";
  const pucModificar = tincPermis(rolUsuari, "MODIFICAR");
  const pucEsborrarFitxa = tincPermis(rolUsuari, "ESBORRAR");

  const isReadOnly = mode === "consultar" || mode === "esborrar";
  const isCreate = mode === "nou";
  const isEdit = mode === "modificar";
  const [menuAccions, setMenuAccions] = useState(false);

  TeclaEscEnter(onCancel, null);

  // ──────────────────────────────────────────────────────────────────────────
  // STATE AGENDA
  // ──────────────────────────────────────────────────────────────────────────
  const [tipus, setTipus] = useState("Avis");
  const [titol, setTitol] = useState("");
  const [estat, setEstat] = useState("Pendent");
  const [prioritat, setPrioritat] = useState(2);
  const [tipusContacte, setTipusContacte] = useState("Trucada");
  const [descripcio, setDescripcio] = useState("");
  const [dataInici, setDataInici] = useState("");
  const [horaInici, setHoraInici] = useState("");
  const [dataFinal, setDataFinal] = useState("");
  const [horaFinal, setHoraFinal] = useState("");

  // Assignació / visibilitat
  const [users, setUsers] = useState([]);
  const [dirigitA, setDirigitA] = useState(0); // 0 = sense assignar
  const [dirigitATouched, setDirigitATouched] = useState(false);
  const [nivellPermis, setNivellPermis] = useState(0); // Privat per defecte

  // ──────────────────────────────────────────────────────────────────────────
  // ENTITATS / CONTACTES + FILTRE
  // ──────────────────────────────────────────────────────────────────────────
  const [filterText, setFilterText] = useState("");
  const [loadingLlista, setLoadingLlista] = useState(false);
  const [entitats, setEntitats] = useState([]); // només entitats
  const [contactes, setContactes] = useState([]); // només contactes (no entitats)
  const [entsById, setEntsById] = useState(new Map());

  const [idEntitat, setIdEntitat] = useState(null);
  const [idContacte, setIdContacte] = useState(null);
  const [origenEntitat, setOrigenEntitat] = useState(""); // Client/Proveïdor/…

  // ──────────────────────────────────────────────────────────────────────────
  // DADES DE CONTACTE (inclou DePartDe / NomRelacionat)
  // ──────────────────────────────────────────────────────────────────────────
  const [nomRelacionat, setNomRelacionat] = useState(""); // De part de
  const [contactNom, setContactNom] = useState("");
  const [contactCognom1, setContactCognom1] = useState("");
  const [contactCognom2, setContactCognom2] = useState("");
  const [contactTelefon, setContactTelefon] = useState("");
  const [contactEmail, setContactEmail] = useState("");

  const [mostrarBlocContacte, setMostrarBlocContacte] = useState(false);
  const [crearModificarContacte, setCrearModificarContacte] = useState(false);

  // Camps no imprescindibles segons Tipus (Avis vs Tasca):
  // - Un Avís gairebé sempre ve d'algú que truca/escriu: mostrem relació i dades de contacte per defecte.
  // - Una Tasca interna normalment no en necessita, però es pot obrir manualment si cal.
  const [mostrarRelacioManual, setMostrarRelacioManual] = useState(null); // null = automàtic segons Tipus
  const [mostrarBlocContacteTouched, setMostrarBlocContacteTouched] = useState(false);

  // ──────────────────────────────────────────────────────────────────────────
  // ADREÇA (mode transitori com a Pressupostos)
  // ──────────────────────────────────────────────────────────────────────────
  const [editAddress, setEditAddress] = useState(false);
  const [adrecaEsNovaDelContacte, setAdrecaEsNovaDelContacte] = useState(false);
  const [adrecaModificada, setAdrecaModificada] = useState(false);

  // valors “finals”
  const [tipusVia, setTipusVia] = useState("");
  const [nomVia, setNomVia] = useState("");
  const [numVia, setNumVia] = useState("");
  const [escalaVia, setEscalaVia] = useState("");
  const [pis, setPis] = useState("");
  const [porta, setPorta] = useState("");
  const [poblacio, setPoblacio] = useState("");
  const [provincia, setProvincia] = useState("");
  const [pais, setPais] = useState("");
  const [codiPostal, setCodiPostal] = useState("");
  const [referencies, setReferencies] = useState("");

  // valors “default” (gris/transitori)
  const [tipusViaDefault, setTipusViaDefault] = useState("");
  const [nomViaDefault, setNomViaDefault] = useState("");
  const [numViaDefault, setNumViaDefault] = useState("");
  const [escalaViaDefault, setEscalaViaDefault] = useState("");
  const [pisDefault, setPisDefault] = useState("");
  const [portaDefault, setPortaDefault] = useState("");
  const [poblacioDefault, setPoblacioDefault] = useState("");
  const [provinciaDefault, setProvinciaDefault] = useState("");
  const [paisDefault, setPaisDefault] = useState("");
  const [codiPostalDefault, setCodiPostalDefault] = useState("");
  const [referenciesDefault, setReferenciesDefault] = useState("");

  const markDirty = () => setAdrecaModificada(true);

  // Reiteracions / Logs
  const [logs, setLogs] = useState([]);
  const [marcarReiteracio, setMarcarReiteracio] = useState(false);
  const [notesReiteracio, setNotesReiteracio] = useState("");
  const [numReiteracions, setNumReiteracions] = useState(0);

  // ──────────────────────────────────────────────────────────────────────────
  // ESTAT DEDUP (Buscarcontacte.php)
  // ──────────────────────────────────────────────────────────────────────────
  const [dedupDone, setDedupDone] = useState(false);
  const [dedupModalOpen, setDedupModalOpen] = useState(false);
  const [dedupContactes, setDedupContactes] = useState([]);
  const [dedupEntitats, setDedupEntitats] = useState([]);
  const [dedupMode, setDedupMode] = useState(null); // "singleContact" | "multi"
  const [dedupStrongContact, setDedupStrongContact] = useState(null);

  // ──────────────────────────────────────────────────────────────────────────
  // DERIVATS PER CONTROL D'EDICIÓ
  // ──────────────────────────────────────────────────────────────────────────
  const isExistingContact = !!idContacte;
  // Només editem les dades del contacte si és nou o si hem marcat "Modificar contacte"
  const canEditContactData = !isExistingContact || crearModificarContacte;
  // Només podem marcar "Modificar l'adreça del contacte" si estem modificant contacte existent
  const canToggleContactAddress = isExistingContact;

  // ──────────────────────────────────────────────────────────────────────────
  // LOAD LLISTES I ITEM
  // ──────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchAll = async () => {
      setLoadingLlista(true);
      try {
        // ENTITATS + CONTACTES
        // ENTITATS + CONTACTES
        const r = await fetch(`${API_URL}/Contactesagenda.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idEmpresa }),
        });
        const d = await r.json();

        const arrEnts = Array.isArray(d?.entitats) ? d.entitats : [];
        const arrConts = Array.isArray(d?.contactes) ? d.contactes : [];

        // RELACIÓ N:M Contactes ↔ Entitats (contracte: objecte { "22":[119,537], ... })
        const relObj = d?.relacionsContactesEntitats || {};
        const relMap = new Map(); // idContacte (Number) -> Set(idEntitat)

        for (const [cidKey, entitatsArr] of Object.entries(relObj)) {
          const cid = Number(cidKey);
          if (!cid) continue;

          const set = new Set(
            Array.isArray(entitatsArr) ? entitatsArr.map((x) => Number(x)).filter(Boolean) : []
          );
          relMap.set(cid, set);
        }

        // Normalitza ENTITATS (mantinc el teu patró habitual d'IdEntitat)
        const ents = arrEnts
          .map((e) => ({
            ...e,
            IdEntitat: e.idEntitats ?? e.IdEntitat ?? null,
            NomMostrar: e.NomMostrar ?? e.NomComercial ?? e.Nom ?? null,
          }))
          .filter((e) => e.IdEntitat != null);

        // Normalitza CONTACTES + hi afegeix EntitatsIds
        const conts = arrConts
          .map((c) => {
            const id = c.idContactes ?? c.Id ?? c.id ?? null;
            const entitatsIds = id ? Array.from(relMap.get(Number(id)) || []) : [];

            return {
              tipus: "contacte",
              Id: id,
              EntitatsIds: entitatsIds,

              Nom: c.Nom ?? "",
              Cognom1: c.Cognom1 ?? "",
              Cognom2: c.Cognom2 ?? "",
              Rol: c.Rol ?? "Altres",
              Telefon: c.Telefon ?? c.TelefonMobil ?? c.TelefonFixe ?? "",
              Email: c.Email ?? "",

              TipusVia: c.TipusVia ?? "",
              NomVia: c.NomVia ?? "",
              NumVia: c.NumVia ?? c.Numero ?? "",
              EscalaVia: c.EscalaVia ?? c.Escala ?? "",
              Pis: c.Pis ?? "",
              Porta: c.Porta ?? "",
              Poblacio: c.Poblacio ?? "",
              Provincia: c.Provincia ?? "",
              Pais: c.Pais ?? "",
              CodiPostal: c.CodiPostal ?? "",
              Referencies: c.Referencies ?? "",
              NomMostrar: c.NomMostrar ?? null,
            };
          })
          .filter((x) => x.Id != null);


        setEntitats(ents);
        setContactes(conts);
        setEntsById(new Map(ents.map((e) => [e.IdEntitat, e])));
        // USUARIS
        try {
          const ru = await fetchAutenticat(`${API_URL}/AgendaUsuaris.php`, {
            method: "POST",
            body: JSON.stringify({
              idEmpresa,
              ambit: "assignacio",
            }),
          });
          const du = await ru.json();
          const arrU = Array.isArray(du?.usuaris) ? du.usuaris : [];
          const normU = arrU
            .map((u) => ({ idUsuari: u.idUsuaris, NomUsuari: u.NomUsuari }))
            .filter((u) => u.idUsuari != null);
          setUsers(normU);
        } catch (e) {
          if (e instanceof SessioCaducadaError) return;
          setUsers([]);
        }
      } catch {
        setEntitats([]);
        setContactes([]);
        setUsers([]);
      } finally {
        setLoadingLlista(false);
      }
    };
    if (idEmpresa) fetchAll();
  }, [idEmpresa]);

  // Carrega LOG si tenim item existent
  useEffect(() => {
    const fetchLog = async () => {
      if (!item?.idAgenda) {
        setLogs([]);
        return;
      }
      try {
        const r = await fetchAutenticat(`${API_URL}/Agendalog.php`, {
          method: "POST",
          body: JSON.stringify({
            op: "Llistar",
            idAgenda: item.idAgenda,
            idEmpresa,
          }),
        });
        const d = await r.json();
        const l = Array.isArray(d?.logs) ? d.logs : [];
        setLogs(l);
        const n = l.filter((x) => x.Accio === "REITERACIO").length;
        setNumReiteracions(n);
      } catch (e) {
        if (e instanceof SessioCaducadaError) return;
        setLogs([]);
        setNumReiteracions(0);
      }
    };
    fetchLog();
  }, [item?.idAgenda, idEmpresa]);

  // Prefill de l’ITEM
  useEffect(() => {
    if (isCreate) {
      resetForm();
      if (forceTasca) {
        setTipus("Tasca");
        // Seed de DataInici venguda del calendari: 'YYYY-MM-DD HH:MM'
        const seed = typeof item?.DataInici === "string" ? item.DataInici.trim() : "";
        if (seed) {
          const [d, t] = seed.split(" ");
          if (d) setDataInici(d);
          if (t) setHoraInici(t.slice(0, 5));
        }
      }
      return;
    }
    if (!item) return;

    // Evitem que un filtre anterior deixi l'Entitat/Contacte seleccionats fora
    // de les opcions visibles i aparentin "buits".
    setFilterText("");

    // Bàsics
    setTipus(item.Tipus || "Avis");
    setTitol(item.Titol || "");
    setEstat(item.Estat || "Pendent");
    setPrioritat(item.Prioritat ?? 2);
    setDescripcio(item.Descripcio || "");

    // Dates tasca
    if (item.Tipus === "Tasca") {
      if (item.DataInici) {
        const d = new Date(item.DataInici);
        setDataInici(
          `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(d.getDate()).padStart(2, "0")}`
        );
        setHoraInici(
          `${String(d.getHours()).padStart(2, "0")}:${String(
            d.getMinutes()
          ).padStart(2, "0")}`
        );
      } else {
        setDataInici("");
        setHoraInici("");
      }
      if (item.DataFinal) {
        const d2 = new Date(item.DataFinal);
        setDataFinal(
          `${d2.getFullYear()}-${String(d2.getMonth() + 1).padStart(
            2,
            "0"
          )}-${String(d2.getDate()).padStart(2, "0")}`
        );
        setHoraFinal(
          `${String(d2.getHours()).padStart(2, "0")}:${String(
            d2.getMinutes()
          ).padStart(2, "0")}`
        );
      } else {
        setDataFinal("");
        setHoraFinal("");
      }
    } else {
      setDataInici("");
      setHoraInici("");
      setDataFinal("");
      setHoraFinal("");
    }

    // Dirigit / Permís
    setDirigitA(item.DirigitA ?? 0);
    setDirigitATouched(false);
    setNivellPermis(item.NivellPermis ?? 0);

    // Origen
    setIdEntitat(
      item.Entitats_idEntitats != null ? Number(item.Entitats_idEntitats) : null
    );
    setIdContacte(
      item.Contactes_idContactes != null ? Number(item.Contactes_idContactes) : null
    );
    setOrigenEntitat(item.Rol || "");

    // Bloc contacte
    setNomRelacionat(item.NomRelacionat || "");
    setContactNom(item.Nom || "");
    setContactCognom1(item.Cognom1 || "");
    setContactCognom2(item.Cognom2 || "");

    // Punt 1 (bug): registres antics amb Entitat però sense Contacte podien
    // omplir Tel/Email del bloc de contacte amb dades "mostrades" (p.ex. de l'Entitat).
    // Si no hi ha contacte seleccionat i tampoc hi ha identitat explícita,
    // deixem el contacte en blanc.
    const teEntitatItem = item.Entitats_idEntitats != null;
    const teContacteItem = item.Contactes_idContactes != null;
    const teIdentitatItem = !!(
      (item.Nom && String(item.Nom).trim()) ||
      (item.Cognom1 && String(item.Cognom1).trim()) ||
      (item.Cognom2 && String(item.Cognom2).trim())
    );

    if (teEntitatItem && !teContacteItem && !teIdentitatItem) {
      setContactTelefon("");
      setContactEmail("");
    } else {
      setContactTelefon(item.TelefonMostrat || "");
      setContactEmail(item.EmailMostrat || "");
    }
    setMostrarBlocContacte(false);
    setMostrarBlocContacteTouched(false);
    setMostrarRelacioManual(null);
    setCrearModificarContacte(false);

    // Adreça (default gris + valors en blanc)
    setTipusVia("");
    setTipusViaDefault(item.TipusVia || "");
    setNomVia("");
    setNomViaDefault(item.NomVia || "");
    setNumVia("");
    setNumViaDefault(item.NumVia || item.Numero || "");
    setEscalaVia("");
    setEscalaViaDefault(item.EscalaVia || item.Escala || "");
    setPis("");
    setPisDefault(item.Pis || "");
    setPorta("");
    setPortaDefault(item.Porta || "");
    setPoblacio("");
    setPoblacioDefault(item.Poblacio || "");
    setProvincia("");
    setProvinciaDefault(item.Provincia || "");
    setPais("");
    setPaisDefault(item.Pais || "");
    setCodiPostal("");
    setCodiPostalDefault(item.CodiPostal || "");
    setReferencies("");
    setReferenciesDefault(item.Referencies || "");
    setEditAddress(false);
    setAdrecaModificada(false);
    setAdrecaEsNovaDelContacte(false);

    // Reset dedup per aquest ítem
    setDedupDone(false);
    setDedupModalOpen(false);
    setDedupContactes([]);
    setDedupEntitats([]);
    setDedupMode(null);
    setDedupStrongContact(null);
  }, [item, isCreate]);

  useEffect(() => {
    if (isCreate || !item) return;

    const entId = item.Entitats_idEntitats != null ? Number(item.Entitats_idEntitats) : null;
    const conId = item.Contactes_idContactes != null ? Number(item.Contactes_idContactes) : null;

    let targetEntId = entId;
    if (!targetEntId && conId) {
      const c = contactes.find((x) => x.Id === conId);
      const linkedIds = Array.isArray(c?.EntitatsIds) ? c.EntitatsIds : [];
      if (linkedIds.length === 1) targetEntId = linkedIds[0];
    }

    if (conId) setIdContacte(conId);

    // FIX: Address Fallback logic when loading existing Agenda item.
    // If the Agenda explicitly has an address (item.TipusVia), we use that (already set in previous effect).
    // If NOT, we fallback to Contact or Entity address to show as default (grey).
    if (!item.TipusVia && !item.NomVia && !item.NumVia) {
      let addrSrc = null;

      if (conId) {
        const c = contactes.find((x) => x.Id === conId);
        if (c && hasAddress(c)) addrSrc = c;
      }

      if (!addrSrc && targetEntId) {
        const e = entsById.get(targetEntId) || entitats.find((x) => x.IdEntitat === targetEntId);
        if (e && hasAddress(e)) addrSrc = e;
      }

      if (addrSrc) {
        setTipusViaDefault(addrSrc.TipusVia || "");
        setNomViaDefault(addrSrc.NomVia || "");
        setNumViaDefault(addrSrc.NumVia || addrSrc.Numero || "");
        setEscalaViaDefault(addrSrc.EscalaVia || addrSrc.Escala || "");
        setPisDefault(addrSrc.Pis || "");
        setPortaDefault(addrSrc.Porta || "");
        setPoblacioDefault(addrSrc.Poblacio || "");
        setProvinciaDefault(addrSrc.Provincia || "");
        setPaisDefault(addrSrc.Pais || "");
        setCodiPostalDefault(addrSrc.CodiPostal || "");
        setReferenciesDefault(addrSrc.Referencies || "");
      }
    }

  }, [isCreate, item, entitats, contactes, entsById]);

  // Si canviem a Tasca i és nou, omplim inici/hora actuals i “dirigit a” usuari
  useEffect(() => {
    if (isReadOnly) return;
    if (tipus !== "Tasca") return;

    if (!dataInici) setDataInici(nowDate());
    if (!horaInici) setHoraInici(nowTime());
  }, [tipus]); // només en canviar a Tasca

  useEffect(() => {
    if (isReadOnly || !isCreate) return;
    if (dirigitATouched) return;
    if (currentUserId != null) setDirigitA(currentUserId);
  }, [tipus, dirigitATouched, currentUserId, isReadOnly, isCreate]);

  // Per defecte: bloc de "Dades de contacte" obert en un Avís (sol venir d'algú
  // que truca/escriu) i tancat en una Tasca interna, mentre l'usuari no ho toqui a mà.
  useEffect(() => {
    if (isReadOnly) return;
    if (mostrarBlocContacteTouched) return;
    setMostrarBlocContacte(tipus === "Avis");
  }, [tipus, mostrarBlocContacteTouched, isReadOnly]);

  // ──────────────────────────────────────────────────────────────────────────
  // VALIDACIÓ
  // ──────────────────────────────────────────────────────────────────────────
  const validate = () => {
    const errors = [];

    // Bàsics
    if (!titol.trim()) errors.push("*Títol obligatori");

    if ((Number(nivellPermis) === 0 || Number(nivellPermis) === 1) && !dirigitA) {
      errors.push("*Privat i Equip exigeixen un usuari assignat");
    }

    // Tasca: coherència dates
    if (tipus === "Tasca") {
      if (!dataInici) {
        errors.push("*Data d'inici obligatòria (Tasca)");
      }

      if (dataInici && dataFinal) {
        const startDt = new Date(`${dataInici}T${(horaInici || "00:00")}:00`);
        const endDt = new Date(`${dataFinal}T${(horaFinal || "00:00")}:00`);

        if (!(endDt > startDt)) {
          errors.push("*La data/hora final ha de ser posterior a la d'inici");
        }
      }
    }

    // ──────────────────────────────────────────────────────────────────────
    // Validació de contacte (després del deduplicat i abans de guardar)
    // ──────────────────────────────────────────────────────────────────────
    const nomForm = (contactNom || "").trim();
    const cognom1Form = (contactCognom1 || "").trim();
    const cognom2Form = (contactCognom2 || "").trim();
    const telForm = (contactTelefon || "").trim();
    const mailForm = (contactEmail || "").trim();

    const esAvis = tipus === "Avis";

    const teIdentitat = !!(nomForm || cognom1Form);
    const teViaContacte = !!(telForm || mailForm);
    const teEntitatOContacte = !!idEntitat || !!idContacte;

    // 1) Regla global: no permetre telèfon/email sols sense identitat
    if (teViaContacte && !teIdentitat) {
      errors.push(
        "Cal informar com a mínim el nom o el primer cognom si poses telèfon o email."
      );
    }

    // 2) Cas especial: AVÍS sense Entitat ni Contacte
    if (esAvis && !teEntitatOContacte) {
      if (!teIdentitat || !teViaContacte) {
        errors.push(
          "Per donar d'alta un avís sense entitat ni contacte seleccionats cal informar com a mínim el nom o primer cognom i un telèfon o email."
        );
      }
    }

    // 3) Regla de contacte: si creem/modifiquem contacte (explícit o automàtic), identitat obligatòria
    const hiHaDadesBasiques = !!(
      nomForm ||
      cognom1Form ||
      cognom2Form ||
      telForm ||
      mailForm
    );
    const autoCrearContacte = !idContacte && hiHaDadesBasiques;

    if ((crearModificarContacte || autoCrearContacte) && !teIdentitat) {
      errors.push(
        "Per crear o modificar un contacte cal informar com a mínim el nom o el primer cognom."
      );
    }

    return errors;
  };


  // ──────────────────────────────────────────────────────────────────────────
  // HELPERS GENERALS
  // ──────────────────────────────────────────────────────────────────────────
  const nowDate = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };
  const nowTime = () => {
    const d = new Date();
    const hh = String(d.getHours()).padStart(2, "0");
    const mi = String(d.getMinutes()).padStart(2, "0");
    return `${hh}:${mi}`;
  };

  const addressSummary = () => {
    const partBase = generateAddress({
      TipusVia: tipusVia || tipusViaDefault,
      NomVia: nomVia || nomViaDefault,
      NumVia: numVia || numViaDefault,
      EscalaVia: escalaVia || escalaViaDefault,
      Pis: pis || pisDefault,
      Porta: porta || portaDefault,
    });
    const tail = [codiPostal || codiPostalDefault, poblacio || poblacioDefault]
      .filter(Boolean)
      .join(" ");
    const rest = [provincia || provinciaDefault, pais || paisDefault]
      .filter(Boolean)
      .join(", ");
    return [partBase, tail, rest].filter(Boolean).join(", ");
  };

  const entitatLabelTitle = () => {
    const base = "Entitat";
    if (!idEntitat) return base;
    return origenEntitat ? `${base} (${origenEntitat})` : base;
  };

  const nivellLabel = (n) =>
    mapejos.nivellPermisAgenda[Number(n)] ?? "Privat";
  const prioritatLabel = (p) =>
    ({ 3: "Màxima", 2: "Alta", 1: "Mitja", 0: "Baixa" }[Number(p)] ?? "—");
  const userNameById = (id) =>
    id && id !== 0
      ? users.find((u) => u.idUsuari === id)?.NomUsuari || `usuari #${id}`
      : "Sense assignar";

  const entitatNomMostrar = (() => {
    if (!idEntitat) return null;
    const e =
      entsById.get(idEntitat) ||
      entitats.find((x) => x.IdEntitat === idEntitat);
    return (
      e?.NomMostrar ||
      e?.NomComercial ||
      [e?.Nom, e?.Cognom1].filter(Boolean).join(" ") ||
      null
    );
  })();

  const dataAltaISO = item?.DataCreacio ?? null;
  const dataAcabatISO = item?.DataAcabat ?? null;
  const dataCanviEstatISO = item?.DataCanviEstat ?? null;
  const dataIniciISO = item?.DataInici ?? null;
  const dataFinalISO = item?.DataFinal ?? null;

  // ──────────────────────────────────────────────────────────────────────────
  // DADES PERSONA: helpers per dedup i bloc contacte
  // ──────────────────────────────────────────────────────────────────────────
  const teDadesPersonaBasics = () => {
    return !!(
      (contactNom && contactNom.trim()) ||
      (contactCognom1 && contactCognom1.trim()) ||
      (contactCognom2 && contactCognom2.trim()) ||
      (contactTelefon && contactTelefon.trim()) ||
      (contactEmail && contactEmail.trim())
    );
  };

  const teDadesPersonaAdreca = () => {
    return !!(
      editAddress &&
      adrecaModificada &&
      (tipusVia ||
        nomVia ||
        numVia ||
        poblacio ||
        provincia ||
        pais ||
        codiPostal)
    );
  };

  const teDadesPersona = () => {
    return teDadesPersonaBasics() || teDadesPersonaAdreca();
  };

  // ──────────────────────────────────────────────────────────────────────────
  // SUBMIT REAL (després de dedup)
  // ──────────────────────────────────────────────────────────────────────────
  const finalSubmit = async () => {
    const errs = validate();
    if (errs.length) {
      alert(errs.join("\n"));
      return;
    }

    const blocAgenda = {
      Tipus: tipus,
      Titol: titol,
      Estat: isCreate ? "Pendent" : estat,
      Prioritat: prioritat,
      Descripcio: descripcio,
      DataInici:
        tipus === "Tasca" && dataInici
          ? `${dataInici} ${horaInici || "00:00"}`
          : null,
      DataFinal:
        tipus === "Tasca" && dataFinal
          ? `${dataFinal} ${horaFinal || "00:00"}`
          : null,
      DirigitA: dirigitA ?? 0,
      NivellPermis: nivellPermis ?? 0,
    };

    const hiHaDadesBasiques = teDadesPersonaBasics();
    const autoCrearContacte = !idContacte && hiHaDadesBasiques;

    // Fix: Si l'usuari marca "Modificar l'adreça del contacte", hem d'enviar flag de modificació 
    // perquè Novaagenda.php entri al bloc d'update del contacte.
    const modificantAdrecaContacte = adrecaEsNovaDelContacte && canToggleContactAddress;
    const crearContacteFinal = crearModificarContacte || autoCrearContacte || modificantAdrecaContacte;

    const blocContacte = {
      NomRelacionat: nomRelacionat || "",
      Nom: contactNom || "",
      Cognom1: contactCognom1 || "",
      Cognom2: contactCognom2 || "",
      Telefon: contactTelefon || "",
      Email: contactEmail || "",
      IdContacte: idContacte ?? null,
      CrearModificarContacte: crearContacteFinal ? 1 : 0,
      IdEntitat: idEntitat ?? null,
      Rol: origenEntitat || "Altres",
    };

    const blocAdreca = editAddress
      ? {
        TipusVia: (tipusVia || tipusViaDefault) || "",
        Via: (nomVia || nomViaDefault) || "",
        Numero: (numVia || numViaDefault) || "",
        Escala: (escalaVia || escalaViaDefault) || "",
        Pis: (pis || pisDefault) || "",
        Porta: (porta || portaDefault) || "",
        Poblacio: (poblacio || poblacioDefault) || "",
        Provincia: (provincia || provinciaDefault) || "",
        Pais: (pais || paisDefault) || "",
        CodiPostal: (codiPostal || codiPostalDefault) || "",
        Referencies: (referencies || referenciesDefault) || "",
        // NOMÉS marquem NovaAdreca si realment volem tocar l'adreça del contacte
        NovaAdreca:
          adrecaEsNovaDelContacte && canToggleContactAddress ? 1 : 0,
      }
      : { NovaAdreca: 0 };

    const blocLogBase = {
      TipusContacte: tipus === "Avis" ? tipusContacte : null,
    };

    const destinatariOriginal = item?.DirigitA ?? 0;
    const esTraspassat =
      isEdit && Number(destinatariOriginal) !== Number(dirigitA ?? 0);

    const blocLogNou = {
      ...blocLogBase,
      Accio: "NOU",
      Notes: descripcio || "",
    };
    const blocLogModificar = {
      ...blocLogBase,
      Accio: "MODIFICAT",
    };
    const blocLogTraspassat = esTraspassat
      ? { ...blocLogBase, Accio: "TRASPASSAT" }
      : null;
    const blocLogReiteracio = isEdit && marcarReiteracio
      ? {
        Accio: "REITERACIO",
        TipusContacte: tipusContacte,
        Notes: notesReiteracio || "",
      }
      : null;

    const payload = {
      idEmpresa: idEmpresa || null,
      idAgenda: item?.idAgenda || null,
      idUsuari: currentUserId ?? null,
      Agenda: blocAgenda,
      Contacte: blocContacte,
      Adreca: blocAdreca,
      Log: isCreate ? blocLogNou : blocLogModificar,
      Traspassat: blocLogTraspassat,
      Reiteracio: blocLogReiteracio,
    };

    try {
      if (mode === "nou") {
        const resp = await fetchAutenticat(`${API_URL}/Novaagenda.php`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        const data = await resp.json();
        if (data?.success) {
          alert("Avís/Tasca creat correctament!");
          resetForm();
          onSave?.(data?.registre || data);
        } else {
          alert(`Error en crear: ${data?.message || "Operació fallida"}`);
        }
      } else if (mode === "modificar") {
        const resp = await fetchAutenticat(`${API_URL}/Modificaragenda.php`, {
          method: "POST",
          body: JSON.stringify(payload),
        });
        const data = await resp.json();
        if (data?.success) {
          if (marcarReiteracio) setMarcarReiteracio(false);
          alert("Avís/Tasca modificat correctament!");
          onSave?.(
            data?.registre || { ...item, ...blocAgenda, DirigitA: blocAgenda.DirigitA }
          );
        } else {
          alert(`Error en modificar: ${data?.message || "Operació fallida"}`);
        }
      } else if (mode === "esborrar") {
        if (!item?.idAgenda) {
          alert("No hi ha id d'agenda");
          return;
        }
        const ok = window.confirm(
          "Segur que vols esborrar aquest registre i el seu log?"
        );
        if (!ok) return;
        const resp = await fetchAutenticat(`${API_URL}/Esborraragenda.php`, {
          method: "POST",
          body: JSON.stringify({ idAgenda: item.idAgenda, idEmpresa }),
        });
        const data = await resp.json();
        if (data?.success) {
          alert("Esborrat correctament.");
          onSave?.({ deleted: true, idAgenda: item.idAgenda });
        } else {
          alert(`Error en esborrar: ${data?.message || "Operació fallida"}`);
        }
      }
    } catch (e) {
      if (e instanceof SessioCaducadaError) return;
      console.error("Error a la crida de Agenda:", e);
      alert("S'ha produït un error en el procés.");
    }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // DEDUP (Buscarcontacte.php)
  // ──────────────────────────────────────────────────────────────────────────
  const normalizeStr = (s) => (s || "").toString().trim().toLowerCase();

  const scoreCandidateContact = (c) => {
    let score = 0;
    const t = c.matchType || "";

    if (
      t === "strong_phone" ||
      t === "strong_whatsapp" ||
      t === "strong_email"
    )
      score += 100;
    else if (t === "medium_name") score += 60;
    else if (t && t.startsWith("weak_")) score += 30;

    const nomForm = normalizeStr(contactNom);
    const cognom1Form = normalizeStr(contactCognom1);
    const telForm = normalizeStr(contactTelefon);
    const emailForm = normalizeStr(contactEmail);

    if (nomForm && nomForm === normalizeStr(c.Nom)) score += 10;
    if (cognom1Form && cognom1Form === normalizeStr(c.Cognom1)) score += 8;
    if (telForm && telForm === normalizeStr(c.Telefon)) score += 6;
    if (emailForm && emailForm === normalizeStr(c.Email)) score += 6;

    return score;
  };

  const scoreCandidateEntitat = (e) => {
    let score = 0;
    const t = e.matchType || "";

    if (
      t === "strong_phone" ||
      t === "strong_whatsapp" ||
      t === "strong_email"
    )
      score += 100;
    else if (t === "medium_name") score += 60;
    else if (t && t.startsWith("weak_")) score += 30;

    const nomForm = normalizeStr(contactNom);
    const cognom1Form = normalizeStr(contactCognom1);
    const telForm = normalizeStr(contactTelefon);
    const emailForm = normalizeStr(contactEmail);

    if (nomForm && nomForm === normalizeStr(e.Nom)) score += 8;
    if (cognom1Form && cognom1Form === normalizeStr(e.Cognom1)) score += 6;
    if (telForm && telForm === normalizeStr(e.Telefon)) score += 6;
    if (emailForm && emailForm === normalizeStr(e.Email)) score += 6;

    return score;
  };

  const handleDedupBeforeSubmit = async () => {
    if (idContacte || !idEmpresa) {
      await finalSubmit();
      return;
    }

    const hiHaDadesBasiques = teDadesPersonaBasics();
    if (!hiHaDadesBasiques) {
      await finalSubmit();
      return;
    }

    try {
      const resp = await fetch(`${API_URL}/Buscarcontacte.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          idEmpresa,
          nom: contactNom || "",
          cognom1: contactCognom1 || "",
          cognom2: contactCognom2 || "",
          telefon: contactTelefon || "",
          email: contactEmail || "",
        }),
      });
      const data = await resp.json();

      if (!data?.success) {
        console.error("Error Buscarcontacte.php:", data?.message);
        await finalSubmit();
        return;
      }

      let contactesRaw = Array.isArray(data.contactes) ? data.contactes : [];
      let entitatsRaw = Array.isArray(data.entitats) ? data.entitats : [];

      contactesRaw = contactesRaw
        .map((c) => ({ ...c, __score: scoreCandidateContact(c) }))
        .sort((a, b) => b.__score - a.__score);

      entitatsRaw = entitatsRaw
        .map((e) => ({ ...e, __score: scoreCandidateEntitat(e) }))
        .sort((a, b) => b.__score - a.__score);

      const strongTypes = ["strong_phone", "strong_whatsapp", "strong_email"];
      const strongContactes = contactesRaw.filter((c) =>
        strongTypes.includes(c.matchType)
      );

      if (contactesRaw.length === 0 && entitatsRaw.length === 0) {
        setDedupDone(true);
        await finalSubmit();
        return;
      }

      if (
        strongContactes.length === 1 &&
        contactesRaw.length === 1 &&
        entitatsRaw.length === 0
      ) {
        setDedupContactes(contactesRaw);
        setDedupEntitats([]);
        setDedupStrongContact(strongContactes[0]);
        setDedupMode("singleContact");
        setDedupModalOpen(true);
        setDedupDone(true);
        return;
      }

      setDedupContactes(contactesRaw);
      setDedupEntitats(entitatsRaw);
      setDedupStrongContact(strongContactes[0] || null);
      setDedupMode("multi");
      setDedupModalOpen(true);
      setDedupDone(true);
    } catch (e) {
      console.error("Error a Buscarcontacte.php:", e);
      await finalSubmit();
    }
  };

  const labelContacteDedup = (c) => {
    const nom = [c.Nom, c.Cognom1, c.Cognom2].filter(Boolean).join(" ");
    let entText = "";
    const entIds = Array.isArray(c?.EntitatsIds) ? c.EntitatsIds : [];
    if (entIds.length > 0) {
      entText = entIds
        .map((idE) => entsById.get(Number(idE)) || entitats.find((e) => e.IdEntitat === Number(idE)))
        .filter(Boolean)
        .map((ent) => ent.NomMostrar || ent.NomComercial || [ent.Nom, ent.Cognom1].filter(Boolean).join(" "))
        .join(", ");
    }
    const telMail = [c.Telefon, c.Email].filter(Boolean).join(" · ");
    return `${nom}${entText ? " (" + entText + ")" : ""}${telMail ? " — " + telMail : ""
      }`;
  };

  const labelEntitatDedup = (e) => {
    const nom =
      e.NomMostrar ||
      e.NomComercial ||
      [e.Nom, e.Cognom1].filter(Boolean).join(" ");
    const telMail = [e.Telefon, e.Email].filter(Boolean).join(" · ");
    const rol = e.Rol ? ` [${e.Rol}]` : "";
    const tipusEnt = e.TipusEntitat ? ` (${e.TipusEntitat})` : "";
    return `${nom}${tipusEnt}${rol}${telMail ? " — " + telMail : ""}`;
  };

  const handleDedupUseContact = (c) => {
    setDedupModalOpen(false);
    handleSelectContacte(String(c.idContactes));
    setMostrarBlocContacte(true);
    setMostrarBlocContacteTouched(true);
    setDedupDone(true);
  };

  const handleDedupUseEntitatOnly = (e) => {
    setDedupModalOpen(false);

    handleSelectEntitat(String(e.idEntitats));

    setIdContacte(null);
    setContactNom("");
    setContactCognom1("");
    setContactCognom2("");
    setContactTelefon("");
    setContactEmail("");
    setMostrarBlocContacte(false);
    setMostrarBlocContacteTouched(true);

    setDedupDone(true);
  };

  const handleDedupUseEntitatLinked = (e) => {
    setDedupModalOpen(false);

    handleSelectEntitat(String(e.idEntitats));
    setMostrarBlocContacte(true);
    setMostrarBlocContacteTouched(true);
    setDedupDone(true);
  };

  const handleDedupCreateIndependentNow = async () => {
    setDedupModalOpen(false);
    setDedupDone(true);
    await finalSubmit();
  };

  const handleDedupCancel = () => {
    setDedupModalOpen(false);
    setDedupDone(false);
  };

  const renderDedupModal = () => {
    if (!dedupModalOpen) return null;

    const strongLabel = dedupStrongContact
      ? labelContacteDedup(dedupStrongContact)
      : null;

    return (
      <div
        className="Dashboard-modal-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0,0,0,0.35)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999,
        }}
      >
        <div
          className="Dashboard-modal"
          style={{
            backgroundColor: "#fff",
            padding: "1rem",
            borderRadius: "8px",
            maxWidth: "800px",
            width: "90%",
            maxHeight: "80vh",
            overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}
        >
          <h3>Possible coincidència de contacte</h3>

          {dedupMode === "singleContact" && dedupStrongContact && (
            <>
              <p>
                Sembla que ja tens aquest contacte registrat:
                <br />
                <strong>{strongLabel}</strong>
              </p>
              <p>Què vols fer?</p>
              <div
                className="Dashboard-button-group"
                style={{ gap: 8, marginTop: 8 }}
              >
                <button
                  type="button"
                  className="Dashboard-btn Dashboard-btn-acceptar"
                  onClick={() => handleDedupUseContact(dedupStrongContact)}
                >
                  Fer servir aquest contacte
                </button>
                <button
                  type="button"
                  className="Dashboard-btn Dashboard-btn-acceptar"
                  onClick={handleDedupCreateIndependentNow}
                >
                  Crear contacte nou igualment
                </button>
                <button
                  type="button"
                  className="Dashboard-btn Dashboard-btn-cancelar"
                  onClick={handleDedupCancel}
                >
                  Cancel·lar
                </button>
              </div>
            </>
          )}

          {dedupMode === "multi" && (
            <>
              <p>
                Hi ha diversos contactes i/o entitats que podrien coincidir amb
                les dades indicades.
              </p>

              {dedupContactes.length > 0 && (
                <>
                  <h4>Contactes existents</h4>
                  <ul className="Dashboard-list">
                    {dedupContactes.map((c) => (
                      <li
                        key={`dedup-con-${c.idContactes}`}
                        className="Dashboard-list-item"
                      >
                        <span>{labelContacteDedup(c)}</span>
                        <button
                          type="button"
                          className="Dashboard-btn Dashboard-btn-acceptar"
                          onClick={() => handleDedupUseContact(c)}
                        >
                          Fer servir
                        </button>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              {dedupEntitats.length > 0 && (
                <>
                  <h4>Entitats relacionades</h4>
                  <ul className="Dashboard-list">
                    {dedupEntitats.map((e) => (
                      <li
                        key={`dedup-ent-${e.idEntitats}`}
                        className="Dashboard-list-item"
                      >
                        <span>{labelEntitatDedup(e)}</span>
                        <div
                          className="Dashboard-button-group"
                          style={{ gap: 4 }}
                        >
                          <button
                            type="button"
                            className="Dashboard-btn Dashboard-btn-acceptar"
                            onClick={() => handleDedupUseEntitatOnly(e)}
                          >
                            Fer servir només entitat
                          </button>
                          <button
                            type="button"
                            className="Dashboard-btn Dashboard-btn-acceptar"
                            onClick={() => handleDedupUseEntitatLinked(e)}
                          >
                            Crear contacte vinculat
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </>
              )}

              <p>
                O bé pots ignorar aquestes coincidències i crear un contacte nou
                independent.
              </p>

              <div
                className="Dashboard-button-group"
                style={{ gap: 8, marginTop: 8 }}
              >
                <button
                  type="button"
                  className="Dashboard-btn Dashboard-btn-acceptar"
                  onClick={handleDedupCreateIndependentNow}
                >
                  Crear contacte nou independent
                </button>
                <button
                  type="button"
                  className="Dashboard-btn Dashboard-btn-cancelar"
                  onClick={handleDedupCancel}
                >
                  Cancel·lar
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  // SUBMIT ORQUESTRADOR (dedup + finalSubmit)
  // ──────────────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (mode === "esborrar" || isReadOnly) {
      await finalSubmit();
      return;
    }

    if (dedupDone || idContacte || !teDadesPersona()) {
      await finalSubmit();
      return;
    }

    await handleDedupBeforeSubmit();
  };

  // ──────────────────────────────────────────────────────────────────────────
  // UI READONLY
  // ──────────────────────────────────────────────────────────────────────────
  const renderReadonly = () => {
    const infoLine = (
      <>
        <span className={`Agenda-badge ${tipus === "Tasca" ? "Agenda-badge-tasca" : "Agenda-badge-avis"}`}>
          {tipus === "Tasca" ? "Tasca" : "Avís"}
        </span>{" "}
        {" \u00a0\u00b7\u00a0 "}
        <strong>Estat:</strong> {estat || "—"}{" "}
        {" \u00a0\u00b7\u00a0 "}
        <strong>Creat per:</strong> {item?.NomCreatPer}{" "}
        {" \u00a0\u00b7\u00a0 "}
        <strong>Assignat a:</strong> {userNameById(dirigitA)}{" "}
        {" \u00a0\u00b7\u00a0 "}
        <strong>Qui ho veu:</strong> {nivellLabel(nivellPermis)}
      </>
    );

    const contacteName = [contactNom, contactCognom1].filter(Boolean).join(" ");
    const tel = contactTelefon || "";
    const mail = contactEmail || "";
    const addr = addressSummary() || "";

    const hiHaContacte = !!contacteName;
    const hiHaTel = !!tel;
    const hiHaMail = !!mail;
    const hiHaAdreca = !!addr;

    const reiteracions = logs.filter((l) => l.Accio === "REITERACIO");
    const mostrarReiteracions =
      tipus === "Avis" && reiteracions.length > 0;

    return (
      <>
        <div className="dv2-card">
          <div className="info-inline">
            <strong>Data d’alta:</strong>{" "}
            {dataAltaISO ? formatDateTime(dataAltaISO) : "—"}
          </div>
          <div className="info-inline">
            <strong>Entitat:</strong> {entitatNomMostrar || "—"}
          </div>
        </div>

        <div className="dv2-card">
          <div className="info-inline">{infoLine}</div>
          <div className="info-inline">
            <strong>Prioritat:</strong> {prioritatLabel(prioritat)}
          </div>
          <div className="info-inline">
            <strong>Títol:</strong> {titol || "—"}
          </div>
          <div className="info-inline">
            <strong>Descripció:</strong> {descripcio || "—"}
          </div>
        </div>

        <div className="dv2-card">
          {nomRelacionat?.trim() && (
            <div className="info-inline">
              <strong>De part de:</strong> {nomRelacionat.trim()}
            </div>
          )}
          {hiHaContacte && (
            <div className="info-inline">
              <strong>Contacte:</strong> {contacteName}
            </div>
          )}
          {hiHaTel && (
            <div className="info-inline">
              <strong>Telèfon:</strong> {tel}
            </div>
          )}
          {hiHaMail && (
            <div className="info-inline">
              <strong>Email:</strong> {mail}
            </div>
          )}
          {hiHaAdreca && (
            <div className="info-inline">
              <strong>Adreça:</strong> {addr}
            </div>
          )}
        </div>

        <div className="dv2-card">
          <div className="info-inline">
            <strong>Data Acabat:</strong>{" "}
            {dataAcabatISO ? formatDateTime(dataAcabatISO) : "—"}
          </div>
          <div className="info-inline">
            <strong>Data Canvi d’Estat:</strong>{" "}
            {dataCanviEstatISO ? formatDateTime(dataCanviEstatISO) : "—"}
          </div>
          {tipus === "Tasca" && (
            <>
              <div className="info-inline">
                <strong>Data inici:</strong>{" "}
                {dataIniciISO ? formatDateTime(dataIniciISO) : "—"}
              </div>
              <div className="info-inline">
                <strong>Data final:</strong>{" "}
                {dataFinalISO ? formatDateTime(dataFinalISO) : "—"}
              </div>
            </>
          )}
        </div>

        {mostrarReiteracions && (
          <div className="dv2-card">
            <div className="info-inline">
              <strong>Reiteracions:</strong> ×{reiteracions.length}
            </div>
            <div className="info-inline">
              <strong>Notes:</strong>
              <ul style={{ margin: "4px 0 0 18px" }}>
                {reiteracions.map((l, idx) => (
                  <li key={idx}>
                    {l.Notes || "(sense nota)"}{" "}
                    {l.DataAccio ? `— ${l.DataAccio}` : ""}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {mode === "consultar" && !esPagina && (
          <div className="dv2-footerActions" style={{ marginTop: 8, gap: 8 }}>
            <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onPrev} title="Anterior">
              ‹
            </button>
            <button type="button" className="dv2-btn" onClick={onCancel}>
              Tornar
            </button>
            <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onNext} title="Següent">
              ›
            </button>
            <span style={{ alignSelf: "center" }}>
              {pos != null && total != null ? `${pos} / ${total}` : ""}
            </span>
          </div>
        )}

        {mode === "esborrar" && !esPagina && (
          <div className="dv2-footerActions" style={{ marginTop: 8, gap: 8 }}>
            <button type="button" className="dv2-btn" onClick={onCancel}>
              Cancel·lar
            </button>
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Esborrar
            </button>
          </div>
        )}
      </>
    );
  };

  // ──────────────────────────────────────────────────────────────────────────
  // UI EDITABLE
  // ──────────────────────────────────────────────────────────────────────────
  const entitatsFiltrades = useMemo(() => {
    const f = (filterText || "").toLowerCase().trim();
    if (!f) return entitats;
    return entitats.filter((x) =>
      [
        x.NomComercial,
        x.Nom,
        x.Cognom1,
        x.Rol,
        x.Telefon,
        x.Email,
        x.TipusVia,
        x.NomVia,
        x.NumVia,
        x.Poblacio,
        x.Provincia,
        x.Pais,
        x.CodiPostal,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(f)
    );
  }, [filterText, entitats]);

  // Entitats disponibles per al selector:
  // - Per defecte: totes les entitats filtrades pel text
  // - Si hi ha un contacte seleccionat i aquest està vinculat a 1..N entitats,
  //   limitem la llista a aquestes entitats (cas "Manel" multi-client).
  const entitatsDisponibles = useMemo(() => {
    let list = entitatsFiltrades;
    if (idContacte) {
      const c = contactes.find((x) => x.Id === idContacte);
      const ids = Array.isArray(c?.EntitatsIds) ? c.EntitatsIds : [];
      // Si és contacte lliure (0 entitats) deixem totes visibles
      if (ids.length > 0) {
        list = entitatsFiltrades.filter((e) => ids.includes(e.IdEntitat));
      }
    }
    // No perdre l'entitat ja assignada a l'avís si el filtre o el contacte l'amaguen.
    if (idEntitat && !list.some((e) => Number(e.IdEntitat) === Number(idEntitat))) {
      const sel =
        entsById.get(idEntitat) ||
        entitats.find((e) => Number(e.IdEntitat) === Number(idEntitat));
      if (sel) list = [sel, ...list];
    }
    return list;
  }, [idContacte, contactes, entitatsFiltrades, idEntitat, entsById, entitats]);


  const contactesFiltrats = useMemo(() => {
    // Si tenim Entitat seleccionada, filtrem pel vincle N:M (ContactesEntitats)
    if (idEntitat) {
      const base = contactes.filter((c) =>
        Array.isArray(c.EntitatsIds) ? c.EntitatsIds.includes(idEntitat) : false
      );

      // Per no "perdre" el contacte seleccionat si l'entitat canvia, l'afegim si cal.
      if (idContacte && !base.some((c) => c.Id === idContacte)) {
        const sel = contactes.find((c) => c.Id === idContacte);
        return sel ? [sel, ...base] : base;
      }
      return base;
    }

    const f = (filterText || "").toLowerCase().trim();
    if (!f) return contactes;
    return contactes.filter((x) =>
      [
        x.Nom,
        x.Cognom1,
        x.Cognom2,
        x.Rol,
        x.Telefon,
        x.Email,
        x.TipusVia,
        x.NomVia,
        x.NumVia,
        x.Poblacio,
        x.Provincia,
        x.Pais,
        x.CodiPostal,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(f)
    );
  }, [filterText, contactes, idEntitat, idContacte]);

  const entitatDisplayName = (e) =>
    e.NomComercial || [e.Nom, e.Cognom1].filter(Boolean).join(" ");

  const selectedContact = useMemo(() => {
    if (!idContacte) return null;
    return contactes.find((c) => c.Id === idContacte) || null;
  }, [idContacte, contactes]);

  const entitatsDelContacte = useMemo(() => {
    const ids = Array.isArray(selectedContact?.EntitatsIds)
      ? selectedContact.EntitatsIds
      : [];
    return ids
      .map((id) => entsById.get(id) || entitats.find((e) => e.IdEntitat === id))
      .filter(Boolean);
  }, [selectedContact, entsById, entitats]);

  const contacteTeEntitatsMultiples = entitatsDelContacte.length > 1;


  const handleSelectEntitat = (val) => {
    const id = val ? Number(val) : null;

    // Si mantenim el filtre actiu després de seleccionar, és fàcil que l'Entitat
    // seleccionada quedi fora de les opcions visibles i el <select> aparenti buit.
    setFilterText("");

    setIdEntitat(id);
    setDedupDone(false);
    if (id) {
      const e = entitats.find((x) => x.IdEntitat === id);
      setOrigenEntitat(e?.Rol || "");
      // Prefill adreça des d’Entitat (però sense entrar en mode edició d'adreça)
      setTipusVia("");
      setTipusViaDefault(e?.TipusVia || "");
      setNomVia("");
      setNomViaDefault(e?.NomVia || "");
      setNumVia("");
      setNumViaDefault(e?.NumVia || "");
      setEscalaVia("");
      setEscalaViaDefault(e?.EscalaVia || "");
      setPis("");
      setPisDefault(e?.Pis || "");
      setPorta("");
      setPortaDefault(e?.Porta || "");
      setPoblacio("");
      setPoblacioDefault(e?.Poblacio || "");
      setProvincia("");
      setProvinciaDefault(e?.Provincia || "");
      setPais("");
      setPaisDefault(e?.Pais || "");
      setCodiPostal("");
      setCodiPostalDefault(e?.CodiPostal || "");
      setReferencies("");
      setReferenciesDefault(e?.Referencies || "");
      // IMPORTANT: no activem l'edició d'adreça automàticament
      setEditAddress(false);
      setAdrecaModificada(false);
      setAdrecaEsNovaDelContacte(false);
    } else {
      setOrigenEntitat("");
    }
  };

  const hasAddress = (o) =>
    !!(
      o?.TipusVia ||
      o?.NomVia ||
      o?.NumVia ||
      o?.EscalaVia ||
      o?.Pis ||
      o?.Porta ||
      o?.Poblacio ||
      o?.Provincia ||
      o?.Pais ||
      o?.CodiPostal ||
      o?.Referencies
    );

  const handleSelectContacte = (val) => {
    const id = val ? Number(val) : null;

    // Mateix motiu que a l'Entitat: mantenir el filtre pot amagar opcions i donar
    // la sensació que no s'ha seleccionat res.
    setFilterText("");

    setIdContacte(id);
    setCrearModificarContacte(false);
    setDedupDone(true);

    if (!id) return;

    const c = contactes.find((x) => x.Id === id);

    // Amb N:M, el contacte pot tenir 0/1/n entitats vinculades.
    const linkedIds = Array.isArray(c?.EntitatsIds) ? c.EntitatsIds : [];

    // Regla: si NO hi ha Entitat seleccionada i el contacte només en té UNA,
    // la seleccionem automàticament (cas habitual).
    if (!idEntitat && linkedIds.length === 1) {
      const autoEntId = linkedIds[0];
      setIdEntitat(autoEntId);
      const eAuto =
        entsById.get(autoEntId) ||
        entitats.find((x) => x.IdEntitat === autoEntId);
      setOrigenEntitat(eAuto?.Rol || "");
    }

    setContactNom(c?.Nom || "");
    setContactCognom1(c?.Cognom1 || "");
    setContactCognom2(c?.Cognom2 || "");
    setContactTelefon(c?.Telefon || "");
    setContactEmail(c?.Email || "");

    // Prefill adreça: prioritat Contacte → Entitat seleccionada → si només hi ha 1 entitat del contacte → res
    let addrSrc = c;
    let targetEntId = idEntitat;

    if (!targetEntId && linkedIds.length === 1) targetEntId = linkedIds[0];

    if (!hasAddress(c) && targetEntId) {
      const e =
        entsById.get(targetEntId) ||
        entitats.find((x) => x.IdEntitat === targetEntId);
      if (e && hasAddress(e)) addrSrc = e;
    }

    setTipusVia("");
    setTipusViaDefault(addrSrc?.TipusVia || "");
    setNomVia("");
    setNomViaDefault(addrSrc?.NomVia || "");
    setNumVia("");
    setNumViaDefault(addrSrc?.NumVia || "");
    setEscalaVia("");
    setEscalaViaDefault(addrSrc?.EscalaVia || "");
    setPis("");
    setPisDefault(addrSrc?.Pis || "");
    setPorta("");
    setPortaDefault(addrSrc?.Porta || "");
    setPoblacio("");
    setPoblacioDefault(addrSrc?.Poblacio || "");
    setProvincia("");
    setProvinciaDefault(addrSrc?.Provincia || "");
    setPais("");
    setPaisDefault(addrSrc?.Pais || "");
    setCodiPostal("");
    setCodiPostalDefault(addrSrc?.CodiPostal || "");
    setReferencies("");
    setReferenciesDefault(addrSrc?.Referencies || "");

    // En seleccionar contacte, NO entrem directament en edició d'adreça
    setEditAddress(false);
    setAdrecaModificada(false);
    setAdrecaEsNovaDelContacte(false);
  };

  const resetForm = () => {
    setTipus("Avis");
    setTitol("");
    setEstat("Pendent");
    setPrioritat(2);
    setTipusContacte("Trucada");
    setDescripcio("");
    setDataInici("");
    setHoraInici("");
    setDataFinal("");
    setHoraFinal("");
    setDirigitA(currentUserId || 0);
    setDirigitATouched(false);
    setNivellPermis(0);
    setFilterText("");
    setIdEntitat(null);
    setIdContacte(null);
    setOrigenEntitat("");
    setNomRelacionat("");
    setContactNom("");
    setContactCognom1("");
    setContactCognom2("");
    setContactTelefon("");
    setContactEmail("");
    setMostrarBlocContacte(false);
    setMostrarBlocContacteTouched(false);
    setMostrarRelacioManual(null);
    setCrearModificarContacte(false);

    setEditAddress(false);
    setAdrecaEsNovaDelContacte(false);
    setAdrecaModificada(false);

    setTipusVia("");
    setTipusViaDefault("");
    setNomVia("");
    setNomViaDefault("");
    setNumVia("");
    setNumViaDefault("");
    setEscalaVia("");
    setEscalaViaDefault("");
    setPis("");
    setPisDefault("");
    setPorta("");
    setPortaDefault("");
    setPoblacio("");
    setPoblacioDefault("");
    setProvincia("");
    setProvinciaDefault("");
    setPais("");
    setPaisDefault("");
    setCodiPostal("");
    setCodiPostalDefault("");
    setReferencies("");
    setReferenciesDefault("");

    setMarcarReiteracio(false);
    setNotesReiteracio("");
    setNumReiteracions(0);

    setDedupDone(false);
    setDedupModalOpen(false);
    setDedupContactes([]);
    setDedupEntitats([]);
    setDedupMode(null);
    setDedupStrongContact(null);
  };

  // Una Tasca interna normalment no necessita cap client/proveïdor/contacte
  // relacionat; per un Avís gairebé sempre en tenim un. Es pot obrir/tancar a mà.
  const mostrarRelacio =
    mostrarRelacioManual !== null
      ? mostrarRelacioManual
      : tipus === "Avis" || !!idEntitat || !!idContacte;

  const camp = (label, required, content, mida = "mig") => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const check = (label, checked, onChange, disabled = false) =>
    camp(
      label,
      false,
      <span className="dv2-toggleRow">
        <span className="dv2-toggle">
          <input type="checkbox" checked={!!checked} disabled={disabled} onChange={onChange} />
          <span className="dv2-toggle-track" />
        </span>
      </span>,
      "curt"
    );

  const inputAdreca = (valor, defecte, setValor, setDefecte, maxLength) => {
    const esTrans = valor === "" && !!defecte;
    return (
      <input
        type="text"
        className={`dv2-input${esTrans ? " is-placeholder" : ""}`}
        value={valor || defecte}
        maxLength={maxLength}
        onChange={(e) => {
          const v = e.target.value;
          setAdrecaModificada(true);
          setDedupDone(false);
          if (valor === "" && defecte) {
            setValor(v.replace(defecte, ""));
            setDefecte("");
          } else {
            setValor(v);
          }
        }}
        onClick={(e) => {
          if (valor === "" && defecte) e.target.setSelectionRange(0, 0);
        }}
      />
    );
  };

  const contactInput = (valor, setValor) => (
    <input
      className={`dv2-input${!canEditContactData ? " is-readonly" : ""}`}
      value={valor}
      readOnly={!canEditContactData}
      onChange={(e) => {
        if (!canEditContactData) return;
        setValor(e.target.value);
        setDedupDone(false);
      }}
    />
  );

  const renderEditable = () => (
    <>
      <section className="dv2-card Agenda-fitxa-camps">
        <div className="dv2-card-head">
          <span className="dv2-card-num">1</span>
          <h2 className="dv2-card-title">{forceTasca ? "Tasca" : "Avís o Tasca"}</h2>
        </div>
        <div className="dv2-fields dv2-fields--sizes">
          <div className="dv2-field dv2-field--mig">
            <span className="dv2-label">Tipus</span>
            {forceTasca ? (
              <span className="Agenda-badge Agenda-badge-tasca">Tasca</span>
            ) : (
              <div className="Agenda-tipus-toggle">
                <button
                  type="button"
                  className={`Agenda-badge Agenda-badge-avis ${tipus === "Avis" ? "" : "Agenda-badge-inactiu"}`}
                  onClick={() => setTipus("Avis")}
                >
                  Avís
                </button>
                <button
                  type="button"
                  className={`Agenda-badge Agenda-badge-tasca ${tipus === "Tasca" ? "" : "Agenda-badge-inactiu"}`}
                  onClick={() => setTipus("Tasca")}
                >
                  Tasca
                </button>
              </div>
            )}
          </div>
          {camp(
            "Títol",
            true,
            <input className="dv2-input" value={titol} onChange={(e) => setTitol(e.target.value)} />,
            "llarg"
          )}
          {!isCreate &&
            camp(
              "Estat",
              false,
              <select className="dv2-select" value={estat} onChange={(e) => setEstat(e.target.value)}>
                <option>Pendent</option>
                <option>EnCurs</option>
                <option>Finalitzat</option>
                <option>Rebutjat</option>
              </select>
            )}
          {camp(
            "Prioritat",
            false,
            <select className="dv2-select" value={prioritat} onChange={(e) => setPrioritat(Number(e.target.value))}>
              <option value={3}>Màxima</option>
              <option value={2}>Alta</option>
              <option value={1}>Mitja</option>
              <option value={0}>Baixa</option>
            </select>
          )}
          {tipus === "Avis" &&
            !forceTasca &&
            camp(
              "Tipus de contacte",
              false,
              <select className="dv2-select" value={tipusContacte} onChange={(e) => setTipusContacte(e.target.value)}>
                <option value="Trucada">Trucada</option>
                <option value="Email">Email</option>
                <option value="Whatsapp">Whatsapp</option>
                <option value="Presencial">Presencial</option>
              </select>
            )}
          {camp(
            isEdit ? "Traspassat a" : "Assignat a",
            false,
            <select
              className="dv2-select"
              value={dirigitA ?? 0}
              onChange={(e) => {
                setDirigitATouched(true);
                setDirigitA(Number(e.target.value));
              }}
            >
              {(Number(nivellPermis) === 2 || Number(nivellPermis) === 3) && (
                <option value={0}>— Sense assignar —</option>
              )}
              {users.map((u) => (
                <option key={u.idUsuari} value={u.idUsuari}>
                  {u.NomUsuari}
                </option>
              ))}
            </select>
          )}
          {camp(
            "Qui ho veu",
            false,
            <select
              className="dv2-select"
              value={Number(nivellPermis)}
              onChange={(e) => {
                const n = Number(e.target.value);
                setNivellPermis(n);
                if ((n === 0 || n === 1) && !dirigitA && currentUserId) {
                  setDirigitA(currentUserId);
                }
              }}
            >
              <option value={0}>Privat</option>
              <option value={1}>Equip</option>
              <option value={2}>Empresa</option>
              <option value={3}>Públic</option>
            </select>
          )}
          {isEdit && tipus === "Avis" && check(`Reiteració ×${numReiteracions}`, marcarReiteracio, (e) => setMarcarReiteracio(e.target.checked))}
          {check("Dades de contacte", mostrarBlocContacte, (e) => {
            setMostrarBlocContacteTouched(true);
            setMostrarBlocContacte(e.target.checked);
          })}
          {camp(
            "Descripció",
            false,
            <textarea className="dv2-textarea" rows={3} value={descripcio} onChange={(e) => setDescripcio(e.target.value)} />,
            "ample"
          )}
          {tipus === "Tasca" && (
            <>
              {camp("Data inici", false, <input type="date" className="dv2-input" value={dataInici} onChange={(e) => setDataInici(e.target.value)} />)}
              {camp("Hora inici", false, <input type="time" className="dv2-input" value={horaInici} onChange={(e) => setHoraInici(e.target.value)} />, "curt")}
              {camp("Data final", false, <input type="date" className="dv2-input" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />)}
              {camp("Hora final", false, <input type="time" className="dv2-input" value={horaFinal} onChange={(e) => setHoraFinal(e.target.value)} />, "curt")}
            </>
          )}
        </div>
      </section>

      <section className="dv2-card">
        <div className="dv2-card-head">
          <span className="dv2-card-num">2</span>
          <h2 className="dv2-card-title">Relació</h2>
        </div>
        {!mostrarRelacio ? (
          <button type="button" className="Agenda-link-toggle" onClick={() => setMostrarRelacioManual(true)}>
            + Relacionar amb un client, proveïdor o contacte
          </button>
        ) : (
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Filtre",
              false,
              <input
                className="dv2-input"
                type="text"
                value={filterText}
                onChange={(e) => {
                  setFilterText(e.target.value);
                  setDedupDone(false);
                }}
              />
            )}
            {camp(
              entitatLabelTitle(),
              false,
              <select className="dv2-select" value={idEntitat != null ? String(idEntitat) : ""} onChange={(e) => handleSelectEntitat(e.target.value)}>
                <option value="">— Cap —</option>
                {loadingLlista && <option>Carregant…</option>}
                {entitatsDisponibles.map((e) => (
                  <option key={`ent-${e.IdEntitat}`} value={String(e.IdEntitat)}>
                    {(e.NomMostrar || e.NomComercial || [e.Nom, e.Cognom1].filter(Boolean).join(" ")) +
                      (e.Telefon ? ` — ${e.Telefon}` : "")}
                  </option>
                ))}
              </select>,
              "llarg"
            )}
            {camp(
              "Contactes",
              false,
              <select className="dv2-select" value={idContacte ?? ""} onChange={(e) => handleSelectContacte(e.target.value)}>
                <option value="">— Cap —</option>
                {loadingLlista && <option>Carregant…</option>}
                {contactesFiltrats.map((c) => (
                  <option key={`con-${c.Id}`} value={c.Id}>
                    {(c.NomMostrar || `${c.Nom}${c.Cognom1 ? " " + c.Cognom1 : ""}${c.Cognom2 ? " " + c.Cognom2 : ""}`) +
                      (c.Telefon ? ` — ${c.Telefon}` : "")}
                  </option>
                ))}
              </select>,
              "llarg"
            )}
            {contacteTeEntitatsMultiples && (
              <p className="dv2-hint dv2-field dv2-field--ample">
                Aquest contacte està vinculat a {entitatsDelContacte.length} entitats. Pots seleccionar una entitat a dalt o
                deixar-ho en blanc.
              </p>
            )}
          </div>
        )}
      </section>

      {mostrarBlocContacte && (
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">3</span>
            <h2 className="dv2-card-title">Contacte</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "De part de",
              false,
              <input className="dv2-input" value={nomRelacionat} onChange={(e) => setNomRelacionat(e.target.value)} />,
              "llarg"
            )}
            {idContacte &&
              check("Modificar contacte", crearModificarContacte, (e) => setCrearModificarContacte(e.target.checked))}
            {camp("Nom", false, contactInput(contactNom, setContactNom))}
            {camp("1er Cognom", false, contactInput(contactCognom1, setContactCognom1))}
            {camp("2on Cognom", false, contactInput(contactCognom2, setContactCognom2))}
            {camp("Telèfon", false, contactInput(contactTelefon, setContactTelefon))}
            {camp("Email", false, contactInput(contactEmail, setContactEmail), "llarg")}
            {check("Adreça", editAddress, (e) => setEditAddress(e.target.checked))}
            {check(
              "Modificar l'adreça del contacte",
              adrecaEsNovaDelContacte,
              (e) => setAdrecaEsNovaDelContacte(e.target.checked),
              !adrecaModificada || !canToggleContactAddress
            )}
          </div>
        </section>
      )}

      {mostrarBlocContacte && editAddress && (
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">4</span>
            <h2 className="dv2-card-title">Adreça d'aquest avís o tasca</h2>
          </div>
          <p className="dv2-hint">
            En gris és la del contacte o entitat. Si escrius a sobre, queda només en aquest element, tret que marquis
            «Modificar l'adreça del contacte».
          </p>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Tipus de via",
              true,
              <select
                className="dv2-select"
                value={tipusVia || tipusViaDefault}
                onChange={(e) => {
                  const valor = e.target.value;
                  setAdrecaModificada(true);
                  setDedupDone(false);
                  if (tipusVia === "" && tipusViaDefault !== "") {
                    setTipusVia(valor.replace(tipusViaDefault, ""));
                    setTipusViaDefault("");
                  } else {
                    setTipusVia(valor);
                  }
                }}
              >
                <option value="">Selecciona un tipus de via</option>
                {Object.keys(mapejos.tipusVia).map((option) => (
                  <option key={option} value={option}>
                    {mapToPresentable(option, mapejos.tipusVia)}
                  </option>
                ))}
              </select>
            )}
            {camp("Nom de la via", true, inputAdreca(nomVia, nomViaDefault, setNomVia, setNomViaDefault, 100), "llarg")}
            {camp("Número", false, inputAdreca(numVia, numViaDefault, setNumVia, setNumViaDefault, 10), "curt")}
            {camp("Escala", false, inputAdreca(escalaVia, escalaViaDefault, setEscalaVia, setEscalaViaDefault, 10), "curt")}
            {camp("Pis", false, inputAdreca(pis, pisDefault, setPis, setPisDefault, 10), "curt")}
            {camp("Porta", false, inputAdreca(porta, portaDefault, setPorta, setPortaDefault, 10), "curt")}
            {camp("Població", false, inputAdreca(poblacio, poblacioDefault, setPoblacio, setPoblacioDefault, 100), "llarg")}
            {camp("Província", false, inputAdreca(provincia, provinciaDefault, setProvincia, setProvinciaDefault, 100), "llarg")}
            {camp("País", false, inputAdreca(pais, paisDefault, setPais, setPaisDefault, 50))}
            {camp("Codi postal", false, inputAdreca(codiPostal, codiPostalDefault, setCodiPostal, setCodiPostalDefault, 10), "curt")}
            {camp(
              "Referències",
              false,
              (() => {
                const esTrans = referencies === "" && !!referenciesDefault;
                return (
                  <textarea
                    className={`dv2-textarea${esTrans ? " is-placeholder" : ""}`}
                    value={referencies || referenciesDefault}
                    maxLength={256}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setAdrecaModificada(true);
                      setDedupDone(false);
                      if (referencies === "" && referenciesDefault) {
                        setReferencies(valor.replace(referenciesDefault, ""));
                        setReferenciesDefault("");
                      } else {
                        setReferencies(valor);
                      }
                    }}
                    onClick={(e) => {
                      if (referencies === "" && referenciesDefault) e.target.setSelectionRange(0, 0);
                    }}
                  />
                );
              })(),
              "ample"
            )}
          </div>
        </section>
      )}

      {/* BOTONS (només al modal del calendari; a la fitxa van al dock) */}
      {!esPagina && (
        <div className="dv2-footerActions" style={{ marginTop: "1rem" }}>
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Cancel·lar
          </button>
          <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
            {isCreate ? "Crear" : "Desar"}
          </button>
        </div>
      )}

      {renderDedupModal()}
    </>
  );

  // ──────────────────────────────────────────────────────────────────────────
  // RENDER ROOT
  // ──────────────────────────────────────────────────────────────────────────
  const titolFitxa =
    mode === "nou"
      ? "Nou element d'agenda"
      : mode === "esborrar"
      ? "Esborrar element"
      : mode === "modificar"
      ? "Modificar"
      : titol || "Consulta";
  const subtitolFitxa =
    mode === "nou"
      ? forceTasca
        ? "Nova tasca al calendari. El ? de la capçalera explica la resta."
        : "Tria Avís (recado) o Tasca (feina). El ? de la capçalera explica la resta."
      : mode === "esborrar"
      ? "Aquesta acció no es pot desfer."
      : [tipus === "Tasca" ? "Tasca" : "Avís", estat, titol].filter(Boolean).join(" · ");

  const botoAjuda = onObrirAjuda && (
    <button
      type="button"
      className="dv2-btn dv2-btn--icon dv2-btn--ghost"
      title="Com funciona l'Agenda"
      aria-label="Ajuda"
      onClick={onObrirAjuda}
    >
      ?
    </button>
  );

  const cos = isReadOnly ? renderReadonly() : renderEditable();
  const classeForm = `dv2-entity Agenda-form ${tipus === "Tasca" ? "Agenda-tema-tasca" : "Agenda-tema-avis"}`;

  if (!esPagina) {
    return (
      <form className={`Dashboard-form ${classeForm}`} onSubmit={(e) => e.preventDefault()}>
        {cos}
      </form>
    );
  }

  const potNavegar = mode === "consultar" && typeof onPrev === "function" && typeof onNext === "function";

  return (
    <form className={classeForm} onSubmit={(e) => e.preventDefault()}>
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "🗓️"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titolFitxa}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitolFitxa}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && pucModificar && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && pucEsborrarFitxa && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
          {(mode === "nou" || mode === "modificar") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              {mode === "nou" ? "Desar" : "Desar"}
            </button>
          )}
          {botoAjuda}
        </div>
      </div>

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">Aquesta acció no es pot desfer.</div>
      )}

      <div className="dv2-entity-body">{cos}</div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onPrev} title="Anterior">
                ‹
              </button>
              <span className="dv2-recordNav-info">
                {pos != null && total != null ? `${pos} / ${total}` : ""}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={onNext} title="Següent">
                ›
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default DetallsAgenda;
